import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Loading from './components/Loading';
import RoleBasedRedirect from './components/RoleBasedRedirect';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Templates from './pages/Templates';
import Schedulers from './pages/Schedulers';
import MySignatures from './pages/MySignatures';
import Applications from './pages/Applications';
import AppConfig from './pages/AppConfig';

// Theme
import { theme } from './theme/theme';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireAdmin?: boolean }> = ({ 
  children, 
  requireAdmin = false 
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Main Layout Component
const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 8, // Account for navbar height
          ml: sidebarOpen ? '280px' : 0,
          transition: 'margin-left 0.3s ease',
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          py: 2,
          width: '100%',
          maxWidth: '1400px',
          mx: sidebarOpen ? 'auto' : 'auto',
          transform: sidebarOpen ? 'none' : 'translateX(-140px)'
        }}
      >
        <Box sx={{ 
          width: '100%',
          px: { xs: 2, sm: 3, md: 4 }
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// App Routes Component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RoleBasedRedirect>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </RoleBasedRedirect>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RoleBasedRedirect>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </RoleBasedRedirect>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute requireAdmin>
            <MainLayout>
              <Users />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/templates"
        element={
          <ProtectedRoute requireAdmin>
            <MainLayout>
              <Templates />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/schedulers"
        element={
          <ProtectedRoute requireAdmin>
            <MainLayout>
              <Schedulers />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/my-signatures"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MySignatures />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/applications"
        element={
          <ProtectedRoute requireAdmin>
            <MainLayout>
              <Applications />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/app-config"
        element={
          <ProtectedRoute requireAdmin>
            <MainLayout>
              <AppConfig />
            </MainLayout>
          </ProtectedRoute>
        }
      />

    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
